import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TrashIcon,
  ArrowPathIcon,
  BeakerIcon,
  UserIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
  PlayIcon,
  ClipboardIcon
} from '@heroicons/react/24/outline';
import SelectedPatient from './SelectedPatient';
import savedPatientsData from '../savedPatients.json';
import API_URL from '../config';

// Available FHIR functions that can be used as data sources
const AVAILABLE_FUNCTIONS = [
  { 
    id: 'getPatient', 
    name: 'Get Patient', 
    icon: UserIcon,
    description: 'Retrieve patient demographics and basic information',
    examplePath: 'name.0.given'
  },
  { 
    id: 'getObservations', 
    name: 'Get Patient Observations', 
    icon: BeakerIcon,
    description: 'Retrieve patient observations and lab results',
    examplePath: 'valueQuantity.value'
  },
  { 
    id: 'getDocuments', 
    name: 'Get Documents', 
    icon: DocumentTextIcon,
    description: 'Retrieve patient documents and clinical notes',
    examplePath: 'type.text'
  },
  { 
    id: 'getDiagnosticReports', 
    name: 'Get Diagnostic Reports', 
    icon: ChartBarIcon,
    description: 'Retrieve diagnostic reports and test results',
    examplePath: 'code.text'
  },
  { 
    id: 'getEncounters', 
    name: 'Get Patient Encounters', 
    icon: ClockIcon,
    description: 'Retrieve patient encounters and visits',
    examplePath: 'type.0.text'
  },
  {
    id: 'getPatientConditions',
    name: 'Get Patient Conditions',
    icon: ExclamationTriangleIcon,
    description: 'Retrieve patient conditions and diagnoses',
    examplePath: 'code.text'
  },
  {
    id: 'getPatientImmunizations',
    name: 'Get Patient Immunizations',
    icon: DocumentMagnifyingGlassIcon,
    description: 'Retrieve patient immunization records',
    examplePath: 'vaccineCode.text'
  },
  {
    id: 'getMedications',
    name: 'Get Medications',
    icon: ExclamationTriangleIcon,
    description: 'Retrieve patient medications and prescriptions',
    examplePath: 'medicationCodeableConcept.text'
  },
  {
    id: 'getQuestionnaireResponses',
    name: 'Get Questionnaire Responses',
    icon: ClipboardIcon,
    description: 'Retrieve patient questionnaire responses',
    examplePath: 'questionnaire'
  }
];

function InputBuilder() {
  const [inputs, setInputs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingInput, setEditingInput] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newInput, setNewInput] = useState({
    name: '',
    description: '',
    function: '',
    path: '',
    type: 'string',
    category: '',
    systemUrn: ''
  });

  // Load saved inputs from localStorage on component mount and when storage changes
  useEffect(() => {
    const loadInputs = () => {
      const savedInputs = localStorage.getItem('mlInputs');
      if (savedInputs) {
        setInputs(JSON.parse(savedInputs));
      }
    };

    // Load inputs initially
    loadInputs();

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'mlInputs' && e.newValue !== e.oldValue) {
        loadInputs();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Save inputs to localStorage whenever they change
  useEffect(() => {
    if (inputs.length > 0) {  // Only save if we have inputs
      localStorage.setItem('mlInputs', JSON.stringify(inputs));
    }
  }, [inputs]);

  const handleAddInput = () => {
    if (!newInput.name || !newInput.function || !newInput.path) {
      alert('Please fill in all required fields');
      return;
    }

    // Check for duplicate names (case-insensitive)
    const existingNames = inputs.map(input => input.name.toLowerCase());
    const newNameLower = newInput.name.toLowerCase();
    
    if (!editingInput && existingNames.includes(newNameLower)) {
      alert(`An input with the name "${newInput.name}" already exists. Please choose a unique name.`);
      return;
    }
    
    if (editingInput && existingNames.includes(newNameLower) && editingInput.name.toLowerCase() !== newNameLower) {
      alert(`An input with the name "${newInput.name}" already exists. Please choose a unique name.`);
      return;
    }

    // Validate category for getPatientConditions
    if (newInput.function === 'getPatientConditions' && !newInput.category) {
      alert('Please specify a category for Patient Conditions');
      return;
    }

    // Validate category for getObservations
    if (newInput.function === 'getObservations' && !newInput.category) {
      alert('Please specify a category for Patient Observations');
      return;
    }

    if (editingInput) {
      // Update existing input
      setInputs(inputs.map(input => 
        input.id === editingInput.id ? { ...newInput, id: editingInput.id } : input
      ));
      setEditingInput(null);
    } else {
      // Add new input
      setInputs([...inputs, { ...newInput, id: Date.now() }]);
    }

    // Reset form
    setNewInput({
      name: '',
      description: '',
      function: '',
      path: '',
      type: 'string',
      category: '',
      systemUrn: ''
    });
    setShowAddForm(false);
  };

  const handleEditInput = (input) => {
    setEditingInput(input);
    setNewInput({
      name: input.name,
      description: input.description,
      function: input.function,
      path: input.path,
      type: input.type,
      category: input.category || '',
      systemUrn: input.systemUrn || ''
    });
    setShowAddForm(true);
    
    // Scroll to the top of the page to show the edit form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingInput(null);
    setNewInput({
      name: '',
      description: '',
      function: '',
      path: '',
      type: 'string',
      category: '',
      systemUrn: ''
    });
  };

  const handleDeleteInput = (id) => {
    const updatedInputs = inputs.filter(input => input.id !== id);
    setInputs(updatedInputs);
    
    // Update localStorage
    if (updatedInputs.length > 0) {
      localStorage.setItem('mlInputs', JSON.stringify(updatedInputs));
    } else {
      // If no inputs left, remove the key from localStorage
      localStorage.removeItem('mlInputs');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewInput(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      
      // Set default categories when function is changed
      if (name === 'function') {
        if (value === 'getObservations') {
          updated.category = 'laboratory'; // Default to laboratory
        } else if (value === 'getPatientConditions') {
          updated.category = 'problem-list-item'; // Default to problem-list-item
        } else {
          updated.category = ''; // Clear category for other functions
        }
      }
      
      return updated;
    });
  };

  // Check if the current name is already taken
  const isNameTaken = () => {
    if (!newInput.name) return false;
    const existingNames = inputs.map(input => input.name.toLowerCase());
    const newNameLower = newInput.name.toLowerCase();
    
    if (editingInput) {
      return existingNames.includes(newNameLower) && editingInput.name.toLowerCase() !== newNameLower;
    } else {
      return existingNames.includes(newNameLower);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setTestResults(null);
  };

  const handleTestRun = async () => {
    if (!selectedPatient) {
      alert('Please select a patient first');
      return;
    }

    setLoading(true);
    setTestResults(null);

    try {
      // Prepare the test request payload
      const testRequest = {
        patientId: selectedPatient.id,
        inputs: inputs.map(input => ({
          name: input.name,
          function: input.function,
          path: input.path,
          type: input.type,
          category: input.category,
          systemUrn: input.systemUrn
        }))
      };

      // Debug: Log what we're sending
      console.log('🚀 Sending test request:', testRequest);
      console.log('🚀 Number of inputs being sent:', testRequest.inputs.length);
      console.log('🚀 Input names:', testRequest.inputs.map(i => i.name));

      // Send all inputs to the backend in a single request
      const response = await fetch(`${API_URL}/api/test-inputs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.details?.message || response.statusText);
      }

      const results = await response.json();

      // Debug: Log the raw response from backend
      console.log('🔍 Raw backend response:', results);
      console.log('🔍 Number of results received:', Object.keys(results).length);
      console.log('🔍 Result keys:', Object.keys(results));

      // Create a JSON object with all the results
      const jsonResults = {
        patientId: selectedPatient.id,
        timestamp: new Date().toISOString(),
        inputs: results
      };

      setTestResults(jsonResults);
      
      // Log the results to console for debugging
      console.log('Test Run Results:', jsonResults);
      console.log('🔍 Final testResults.inputs keys:', Object.keys(jsonResults.inputs));
    } catch (error) {
      console.error('Error running test:', error);
      setTestResults({
        error: error.message,
        success: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResults = () => {
    if (testResults) {
      const jsonString = JSON.stringify(testResults, null, 2);
      navigator.clipboard.writeText(jsonString);
      alert('Results copied to clipboard!');
    }
  };

  return (
    <div className="w-[85%] mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              ML Model Input Builder
            </h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Input
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Designate FHIR fields as inputs for machine learning models
          </p>
        </div>
      </div>

      {/* Add/Edit Input Form */}
      {showAddForm && (
        <div className="bg-white shadow sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {editingInput ? 'Edit Input' : 'Add New Input'}
            </h4>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={newInput.name}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm ${
                    isNameTaken() 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                  }`}
                  placeholder="e.g., Patient Age"
                  required
                />
                {isNameTaken() ? (
                  <p className="mt-1 text-sm text-red-600">
                    ⚠️ This name is already taken. Please choose a unique name.
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    Must be unique across all inputs
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <input
                  type="text"
                  name="description"
                  value={newInput.description}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., Patient's age in years"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  FHIR Function <span className="text-red-500">*</span>
                </label>
                <select
                  name="function"
                  value={newInput.function}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  required
                >
                  <option value="">Select a function</option>
                  {AVAILABLE_FUNCTIONS.map(func => (
                    <option key={func.id} value={func.id}>
                      {func.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Data Path <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="path"
                  value={newInput.path}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., resource.valueQuantity.value"
                  required
                />
                {newInput.function && (
                  <p className="mt-1 text-sm text-gray-500">
                    Example path: {AVAILABLE_FUNCTIONS.find(f => f.id === newInput.function)?.examplePath}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Data Type</label>
                <select
                  name="type"
                  value={newInput.type}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="date">Date</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">System URN</label>
                <input
                  type="text"
                  name="systemUrn"
                  value={newInput.systemUrn}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="e.g., http://loinc.org"
                  readOnly={!!newInput.systemUrn}
                />
                {newInput.systemUrn && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ System URN automatically captured from selected path
                  </p>
                )}
                {!newInput.systemUrn && (
                  <p className="mt-1 text-sm text-gray-500">
                    System URN will be automatically populated when adding from JSON path selection
                  </p>
                )}
              </div>
              {newInput.function === 'getPatientConditions' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    name="category"
                    value={newInput.category}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">All Categories</option>
                    <option value="care-plan-problem">Care Plan Problem</option>
                    <option value="dental-finding">Dental Finding</option>
                    <option value="encounter-diagnosis">Encounter Diagnosis</option>
                    <option value="genomics">Genomics</option>
                    <option value="health-concern">Health Concern</option>
                    <option value="problem-list-item">Problem List Item</option>
                    <option value="infection">Infection</option>
                    <option value="medical-history">Medical History</option>
                    <option value="reason-for-visit">Reason For Visit</option>
                  </select>
                </div>
              )}
              {newInput.function === 'getObservations' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    name="category"
                    value={newInput.category}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="exam">Audiology</option>
                    <option value="functional-mental-status">Activities of Daily Living</option>
                    <option value="survey">Assessments</option>
                    <option value="core-characteristics">Core Characteristics</option>
                    <option value="imaging">DICOM Images</option>
                    <option value="social-history">Family Situation</option>
                    <option value="genomic">Genomic</option>
                    <option value="labor-delivery">Labor & Delivery</option>
                    <option value="laboratory">Laboratory</option>
                    <option value="252275004-Hematology">Labs - Hematology</option>
                    <option value="275711006-Chemistry">Labs - Chemistry</option>
                    <option value="68793005-Serology">Labs - Serology</option>
                    <option value="395124008-Virology">Labs - Virology</option>
                    <option value="314076009-Toxicology">Labs - Toxicology</option>
                    <option value="19851009-Microbiology">Labs - Microbiology</option>
                    <option value="405825005-Molecular genetics">Labs - Molecular genetics</option>
                    <option value="LDA">Lines, Drains, Airways</option>
                    <option value="newborn-delivery">Newborn Delivery</option>
                    <option value="obstetrics-gynecology">Obstetrics & Gynecology</option>
                    <option value="periodontal">Periodontal</option>
                    <option value="smartdata">SmartData</option>
                    <option value="vital-signs">Vitals</option>
                  </select>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                onClick={handleAddInput}
                disabled={isNameTaken() || !newInput.name || !newInput.function || !newInput.path}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  isNameTaken() || !newInput.name || !newInput.function || !newInput.path
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                }`}
              >
                {editingInput ? 'Save Changes' : 'Add Input'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inputs List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {inputs.map((input) => {
            const functionInfo = AVAILABLE_FUNCTIONS.find(f => f.id === input.function);
            const Icon = functionInfo?.icon || ArrowPathIcon;
            
            return (
              <li key={input.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Icon className="h-5 w-5 text-gray-400 mr-2" />
                      <p className="text-sm font-medium text-primary-600 truncate">
                        {input.name}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditInput(input)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteInput(input.id)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        {input.description}
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <p>
                        {functionInfo?.name} • {input.type}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 font-mono">
                      {input.path}
                    </p>
                    {input.systemUrn && (
                      <p className="text-sm text-green-600 font-mono mt-1">
                        System: {input.systemUrn}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          {inputs.length === 0 && (
            <li className="px-4 py-5 sm:px-6">
              <div className="text-center text-gray-500">
                No inputs defined yet. Click "Add Input" to create one.
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* Test Run Card */}
      <div className="bg-white shadow sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Test Run
            </h3>
            <div className="flex space-x-3">
              {testResults && (
                <button
                  onClick={handleCopyResults}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <DocumentTextIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                  Copy Results
                </button>
              )}
              <button
                onClick={handleTestRun}
                disabled={loading || inputs.length === 0}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  loading || inputs.length === 0 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                }`}
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Running Test...
                  </>
                ) : (
                  <>
                    <PlayIcon className="-ml-1 mr-2 h-5 w-5" />
                    Run Test
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            {selectedPatient ? (
              <SelectedPatient 
                patient={selectedPatient} 
                onClear={handleClearPatient}
                onSelect={handleSelectPatient}
              />
            ) : (
              <div className="flex items-center space-x-2 bg-primary-50 px-3 py-2 rounded-md">
                <span className="text-sm text-primary-700">Select Patient:</span>
                <select
                  className="text-sm px-2 py-1 rounded border border-primary-200 focus:outline-none"
                  onChange={e => {
                    const patient = savedPatientsData.patients.find(p => p.id === e.target.value);
                    if (patient) handleSelectPatient(patient);
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Select...</option>
                  {savedPatientsData.patients.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {testResults && (
            <div className="mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="mb-4">
                  <div className="text-sm text-gray-500">
                    Patient ID: {testResults.patientId}
                  </div>
                  <div className="text-sm text-gray-500">
                    Timestamp: {new Date(testResults.timestamp).toLocaleString()}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Input</th>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">Values</th>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Type</th>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">System URN</th>
                        <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(testResults.inputs).map(([name, result]) => (
                        <tr key={name}>
                          <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">
                            <div className="truncate max-w-[150px] sm:max-w-[200px]" title={name}>
                              {name}
                            </div>
                          </td>
                          <td className="px-2 sm:px-4 py-4 text-sm text-gray-900">
                            {result.success ? (
                              <div className="space-y-1">
                                {result.values.map((value, index) => (
                                  <div key={index} className="font-mono text-xs sm:text-sm truncate max-w-[200px] sm:max-w-[300px]" title={JSON.stringify(value)}>
                                    {JSON.stringify(value)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-red-600 text-xs sm:text-sm truncate max-w-[200px] sm:max-w-[300px]" title={result.error}>
                                {result.error}
                              </span>
                            )}
                          </td>
                          <td className="px-2 sm:px-4 py-4 text-xs sm:text-sm text-gray-500">{result.type}</td>
                          <td className="px-2 sm:px-4 py-4 text-sm text-gray-500">
                            {result.systemUrn ? (
                              <span className="font-mono text-green-600 text-xs sm:text-sm truncate max-w-[150px] sm:max-w-[200px] block" title={result.systemUrn}>
                                {result.systemUrn}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-2 sm:px-4 py-4 text-sm">
                            {result.success ? (
                              <span className="px-1 sm:px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Success
                              </span>
                            ) : (
                              <span className="px-1 sm:px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Error
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InputBuilder; 